import axios from 'axios';

const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim();
const trimmedBackendUrl = rawBackendUrl.replace(/\/+$/, '');

const isHttpsPage = typeof window !== 'undefined' && window.location.protocol === 'https:';
// On HTTPS pages, always use same-origin API proxy to avoid mixed-content and CORS issues.
const API_URL = isHttpsPage
  ? '/api'
  : (trimmedBackendUrl
      ? (trimmedBackendUrl.endsWith('/api') ? trimmedBackendUrl : `${trimmedBackendUrl}/api`)
      : '/api');

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('admin_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Some deployments expose event admin routes as /events instead of /admin/events.
// Fallback keeps admin UI compatible during backend transition.
const withEventEndpointFallback = async (primaryRequest, fallbackRequest) => {
  try {
    return await primaryRequest();
  } catch (error) {
    if (error?.response?.status === 404) {
      return fallbackRequest();
    }
    throw error;
  }
};

// API Service
export const apiService = {
  // Auth
  login: (credentials) => api.post('/admin/login', credentials),
  logout: () => api.post('/admin/logout'),
  getCurrentAdmin: () => api.get('/admin/me'),

  // Public endpoints
  getBanners: () => api.get('/banners'),
  getAdvertisements: () => api.get('/advertisements'),
  getNews: (params) => api.get('/news', { params }),
  getNewsById: (id) => api.get(`/news/${id}`),
  getPhotos: (params) => api.get('/photos', { params }),
  getVideos: (params) => api.get('/videos', { params }),
  getMatches: (status, category, eventId) => api.get('/matches', { params: { status, category, event_id: eventId } }),
  getMatchById: (id) => api.get(`/matches/${id}`),
  getStandings: (category, eventId) => api.get('/standings', { params: { category, event_id: eventId } }),
  getTopScorers: (category) => api.get('/top-scorers', { params: { category } }),
  getPlayerStats: (category, sortBy, eventId) => api.get('/player-stats', { params: { category, sort_by: sortBy, event_id: eventId } }),
  getParticipants: (params) => api.get('/participants', { params }),
  getParticipantById: (id) => api.get(`/participants/${id}`),
  getPublicEvents: (params) => api.get('/events', { params }),
  getPublicEventById: (id) => api.get(`/events/${id}`),
  getPublicAppConfig: (configType) => api.get(`/app-config/${configType}`),

  // Admin endpoints
  admin: {
    // Generic helpers (used by some admin pages for custom endpoints)
    get: (url, config) => api.get(url, config),
    post: (url, data, config) => api.post(url, data, config),
    put: (url, data, config) => api.put(url, data, config),
    delete: (url, config) => api.delete(url, config),

    // Banners
    getBanners: () => api.get('/admin/banners'),
    createBanner: (formData) => api.post('/admin/banners', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
    updateBanner: (id, formData) => api.put(`/admin/banners/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
    deleteBanner: (id) => api.delete(`/admin/banners/${id}`),

    // Advertisements
    getAdvertisements: () => api.get('/admin/advertisements'),
    createAdvertisement: (formData) => api.post('/admin/advertisements', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
    updateAdvertisement: (id, formData) => api.put(`/admin/advertisements/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
    deleteAdvertisement: (id) => api.delete(`/admin/advertisements/${id}`),

    // News
    getNews: () => api.get('/admin/news'),
    createNews: (formData) => api.post('/admin/news', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
    updateNews: (id, formData) => api.put(`/admin/news/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
    deleteNews: (id) => api.delete(`/admin/news/${id}`),

    // Photos
    getPhotos: () => api.get('/admin/photos'),
    createPhoto: (formData) => api.post('/admin/photos', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
    createPhotosBulk: (formData) => api.post('/admin/photos/bulk', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
    updatePhoto: (id, formData) => api.put(`/admin/photos/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
    deletePhoto: (id) => api.delete(`/admin/photos/${id}`),

    // Videos
    getVideos: () => api.get('/admin/videos'),
    createVideo: (data) => api.post('/admin/videos', data),
    updateVideo: (id, data) => api.put(`/admin/videos/${id}`, data),
    deleteVideo: (id) => api.delete(`/admin/videos/${id}`),

    // Matches
    getMatches: (params) => api.get('/admin/matches', { params }),
    importMatchesExcel: (formData) => api.post('/admin/matches/import-excel', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
    createMatch: (data) => api.post('/admin/matches', data),
    updateMatch: (id, data) => api.put(`/admin/matches/${id}`, data),
    deleteMatch: (id) => api.delete(`/admin/matches/${id}`),
    updateMatchGoals: (id, data) => api.put(`/admin/matches/${id}/goals`, data),

    // Standings
    getStandings: () => api.get('/admin/standings'),
    createStanding: (data) => api.post('/admin/standings', data),
    updateStanding: (id, data) => api.put(`/admin/standings/${id}`, data),
    deleteStanding: (id) => api.delete(`/admin/standings/${id}`),

    // Top Scorers
    getTopScorers: () => api.get('/admin/top-scorers'),
    createTopScorer: (data) => api.post('/admin/top-scorers', data),
    updateTopScorer: (id, data) => api.put(`/admin/top-scorers/${id}`, data),
    deleteTopScorer: (id) => api.delete(`/admin/top-scorers/${id}`),

    // Participants
    getParticipants: (params) => api.get('/admin/participants', { params }),
    getDashboardAnalytics: (params) => api.get('/admin/dashboard-analytics', { params }),
    getAppConfig: () => api.get('/admin/app-config'),
    updateAppConfig: (configType, values) => api.put(`/admin/app-config/${configType}`, { values }),
    createParticipant: (formData) => api.post('/admin/participants', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
    updateParticipant: (id, formData) => api.put(`/admin/participants/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
    updateParticipantPlayers: (id, players) => api.put(`/admin/participants/${id}/players`, players),
    updateParticipantFormation: (id, formation) => api.put(`/admin/participants/${id}/formation`, null, {
      params: { formation }
    }),
    deleteParticipant: (id) => api.delete(`/admin/participants/${id}`),

    // School Reference
    getSchoolReference: (params) => api.get('/admin/school-reference', { params }),

    // Team Reference
    getTeamReference: (params) => api.get('/admin/team-reference', { params }),

    // Club admins and audit logs
    getClubAdmins: () => api.get('/admin/club-admins'),
    createClubAdmin: (data) => api.post('/admin/club-admins', data),
    deleteClubAdmin: (id) => api.delete(`/admin/club-admins/${id}`),
    getAuditLogs: (date, limit = 100) => api.get('/admin/audit-logs', { params: { date, limit } }),
    
    // Settings
    changePassword: (data) => api.post('/admin/change-password', data),

    // Events
    getEvents: () => withEventEndpointFallback(
      () => api.get('/admin/events'),
      () => api.get('/events')
    ),
    getEventById: (id) => withEventEndpointFallback(
      () => api.get(`/admin/events/${id}`),
      () => api.get(`/events/${id}`)
    ),
    createEvent: (data) => withEventEndpointFallback(
      () => api.post('/admin/events', data),
      () => api.post('/events', data)
    ),
    updateEvent: (id, data) => withEventEndpointFallback(
      () => api.put(`/admin/events/${id}`, data),
      () => api.put(`/events/${id}`, data)
    ),
    deleteEvent: (id) => withEventEndpointFallback(
      () => api.delete(`/admin/events/${id}`),
      () => api.delete(`/events/${id}`)
    ),

    // Event Teams
    getEventTeams: (eventId) => withEventEndpointFallback(
      () => api.get(`/admin/events/${eventId}/teams`),
      () => api.get(`/events/${eventId}/teams`)
    ),
    registerTeamToEvent: (eventId, data) => withEventEndpointFallback(
      () => api.post(`/admin/events/${eventId}/teams`, data),
      () => api.post(`/events/${eventId}/teams`, data)
    ),
    unregisterTeamFromEvent: (eventId, registrationId) => withEventEndpointFallback(
      () => api.delete(`/admin/events/${eventId}/teams/${registrationId}`),
      () => api.delete(`/events/${eventId}/teams/${registrationId}`)
    ),
    updateEventTeamStatus: (eventId, registrationId, status) => withEventEndpointFallback(
      () => api.put(`/admin/events/${eventId}/teams/${registrationId}/status`, { status }),
      () => api.put(`/events/${eventId}/teams/${registrationId}/status`, { status })
    ),

    // Event Groups
    getEventGroups: (eventId, category) => withEventEndpointFallback(
      () => api.get(`/admin/events/${eventId}/groups`, { params: { category } }),
      () => api.get(`/events/${eventId}/groups`, { params: { category } })
    ),
    createEventGroup: (eventId, data) => withEventEndpointFallback(
      () => api.post(`/admin/events/${eventId}/groups`, data),
      () => api.post(`/events/${eventId}/groups`, data)
    ),

    // Event Knockout
    getEventKnockout: (eventId, category) => withEventEndpointFallback(
      () => api.get(`/admin/events/${eventId}/knockout`, { params: { category } }),
      () => api.get(`/events/${eventId}/knockout`, { params: { category } })
    ),
    createEventKnockout: (eventId, data) => withEventEndpointFallback(
      () => api.post(`/admin/events/${eventId}/knockout`, data),
      () => api.post(`/events/${eventId}/knockout`, data)
    ),

    // Event Brackets
    getEventBrackets: (eventId) => withEventEndpointFallback(
      () => api.get(`/admin/events/${eventId}/brackets`),
      () => api.get(`/events/${eventId}/brackets`)
    ),
    saveEventBracket: (eventId, data) => withEventEndpointFallback(
      () => api.post(`/admin/events/${eventId}/brackets`, data),
      () => api.post(`/events/${eventId}/brackets`, data)
    ),
    deleteEventBracket: (eventId, bracketId) => withEventEndpointFallback(
      () => api.delete(`/admin/events/${eventId}/brackets/${bracketId}`),
      () => api.delete(`/events/${eventId}/brackets/${bracketId}`)
    ),
    exportEventBracketCsv: (eventId, bracketId) => withEventEndpointFallback(
      () => api.get(`/admin/events/${eventId}/brackets/${bracketId}/export-csv`, { responseType: 'blob' }),
      () => api.get(`/events/${eventId}/brackets/${bracketId}/export-csv`, { responseType: 'blob' })
    ),

    // Event Standings
    getEventStandings: (eventId, category, groupId) => withEventEndpointFallback(
      () => api.get(`/admin/events/${eventId}/standings`, { params: { category, group_id: groupId } }),
      () => api.get(`/events/${eventId}/standings`, { params: { category, group_id: groupId } })
    ),
    createEventStanding: (eventId, data) => withEventEndpointFallback(
      () => api.post(`/admin/events/${eventId}/standings`, data),
      () => api.post(`/events/${eventId}/standings`, data)
    ),
    updateEventStanding: (eventId, standingId, data) => withEventEndpointFallback(
      () => api.put(`/admin/events/${eventId}/standings/${standingId}`, data),
      () => api.put(`/events/${eventId}/standings/${standingId}`, data)
    ),
  },
};

export default api;